import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { EmailDecryptModal } from "./EmailDecryptModal";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "../../contexts/LanguageContext";
import styles from "./Navbar.module.css";
import { TranslationKey } from "../../i18n/translations";

interface NavLink {
  name: string;
  path: string;
  translationKey: string;
}

// Left side navigation items (appear after logo)
const leftNavLinks: NavLink[] = [
  { name: "Home", path: "/", translationKey: "nav.home" },
  { name: "Portfolio", path: "/portfolio", translationKey: "nav.portfolio" },
  { name: "About", path: "/about", translationKey: "nav.about" },
  { name: "Services", path: "/services", translationKey: "nav.services" }
];

// Right side navigation items (appear before book button)
const rightNavLinks: NavLink[] = [];

export const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleBookNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEmailModalOpen(true);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      <nav className={styles.navbar}>
        <div className={styles.container}>
          {/* Logo - Left Aligned */}
          <Link to="/" className={styles.logo}>
            <img
              src="/images/lensklicks-black.webp"
              alt="LensKlicks Photography"
              className={styles.logoImage}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Link>

          {/* Left Navigation Links */}
          <ul className={styles.leftNavLinks}>
            {leftNavLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`${styles.navLink} ${styles.leftNavLink} ${
                    isActive(link.path) ? styles.active : ""
                  }`}
                >
                  {t(link.translationKey as TranslationKey)}
                  <span className={styles.underline} />
                </Link>
              </li>
            ))}
          </ul>

          {/* Spacer to push right items to the end */}
          <div className={styles.spacer} />

          {/* Right Navigation Links */}
          <ul className={styles.rightNavLinks}>
            {rightNavLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`${styles.navLink} ${styles.rightNavLink} ${
                    isActive(link.path) ? styles.active : ""
                  }`}
                >
                  {t(link.translationKey as TranslationKey)}
                  <span className={styles.underline} />
                </Link>
              </li>
            ))}
            <li>
              <LanguageSwitcher />
            </li>
            <li>
              <button
                className={styles.bookButton}
                onClick={handleBookNowClick}
              >
                {t("nav.getInTouch")}
              </button>
            </li>
          </ul>

          {/* Mobile Menu Toggle */}
          <button
            className={styles.mobileToggle}
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span
              className={`${styles.hamburger} ${
                isMobileMenuOpen ? styles.open : ""
              }`}
            />
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`${styles.mobileOverlay} ${
            isMobileMenuOpen ? styles.open : ""
          }`}
          onClick={toggleMobileMenu}
        />

        {/* Mobile Menu */}
        <div
          className={`${styles.mobileMenu} ${
            isMobileMenuOpen ? styles.open : ""
          }`}
        >
          <div className={styles.mobileMenuContent}>
            <ul className={styles.mobileNavLinks}>
              {/* All nav items in mobile */}
              {[...leftNavLinks, ...rightNavLinks].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`${styles.mobileNavLink} ${
                      isActive(link.path) ? styles.active : ""
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t(link.translationKey as TranslationKey)}
                  </Link>
                </li>
              ))}
              <li className={styles.mobileLanguageItem}>
                <LanguageSwitcher />
              </li>
              <li>
                <button
                  className={styles.mobileBookButton}
                  onClick={(e) => {
                    setIsMobileMenuOpen(false);
                    setIsEmailModalOpen(true);
                  }}
                >
                  {t("nav.bookNow")}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Email Decrypt Modal */}
      <EmailDecryptModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
      />
    </>
  );
};
